// Persona inboxes. Mail from aa-app can carry sign-in codes and invite links
// for the personas' dev seats, so viewers see them redacted; only admin
// spectators see codes and can open the original HTML (rendered in a fully
// sandboxed iframe — no scripts, no same-origin access).
import { useState } from 'react';
import type { Doc } from '../../convex/_generated/dataModel';
import { fmtDayTime, fmtRelative, humanize } from '../lib/format';
import { personaKeyForAddress } from '../lib/hooks';
import { useSession } from '../lib/session';
import { Avatar } from '../ui/Avatar';
import { Chip, Empty, Segmented } from '../ui/primitives';

type Email = Doc<'emails'>;

const URL_RE = /\bhttps?:\/\/[^\s<>"')]+/g;

export function redactEmailText(text: string, email: Pick<Email, 'extracted'>, isAdmin: boolean): string {
  if (isAdmin) return text;
  let out = text;
  if (email.extracted.code) out = out.split(email.extracted.code).join('••••••');
  // Links can embed one-time tokens: keep origin + path, drop the query and fragment.
  out = out.replace(URL_RE, (u) => {
    try {
      const url = new URL(u);
      return url.search || url.hash ? `${url.origin}${url.pathname}?…` : u;
    } catch {
      return u;
    }
  });
  return out;
}

const CLASS_TONE: Record<Email['classification'], 'leaf' | 'sun' | 'coral' | 'grape' | 'neutral'> = {
  invite: 'leaf',
  notification: 'coral',
  magic_code: 'grape',
  password_reset: 'sun',
  other: 'neutral',
};

/** srcdoc wrapper: no scripts, no remote fetches except images, links open in a new tab. */
function htmlDoc(html: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src https: data:; style-src 'unsafe-inline'"><base target="_blank"><style>body{font-family:system-ui,sans-serif;margin:12px;color:#222;background:#fff}img{max-width:100%;height:auto}</style></head><body>${html}</body></html>`;
}

export function EmailList({ emails, now, showRecipient }: { emails: Email[] | undefined; now: number; showRecipient?: boolean }) {
  const { isAdmin } = useSession();
  const [openId, setOpenId] = useState<string | null>(null);
  const [mode, setMode] = useState<'text' | 'html'>('text');
  if (emails && emails.length === 0) return <Empty icon="mail" title="Inbox zero">No mail has arrived yet.</Empty>;
  return (
    <ul className="email-list">
      {(emails ?? []).map((e) => {
        const open = openId === e._id;
        const recipientKey = e.to.map(personaKeyForAddress).find(Boolean);
        const senderKey = personaKeyForAddress(e.from);
        return (
          <li key={e._id} className={`email ${e.readAt ? 'is-read' : 'is-unread'} ${open ? 'is-open' : ''}`}>
            <button type="button" className="email-row" onClick={() => setOpenId(open ? null : e._id)} aria-expanded={open}>
              {senderKey ? <Avatar personaKey={senderKey} size={28} /> : <span className="email-src">{e.source === 'aa-app' ? 'aa' : '@'}</span>}
              <span className="email-main">
                <span className="email-subject">{e.subject || '(no subject)'}</span>
                <span className="email-from">
                  {e.from}
                  {showRecipient && recipientKey ? ` → ${recipientKey}` : ''}
                </span>
              </span>
              <span className="email-side">
                <Chip tone={CLASS_TONE[e.classification]}>{humanize(e.classification)}</Chip>
                <time title={fmtDayTime(e.receivedAt)}>{fmtRelative(e.receivedAt, now)}</time>
              </span>
            </button>
            {open ? (
              <div className="email-body">
                <div className="email-headers">
                  <span>
                    <b>From</b> {e.from}
                  </span>
                  <span>
                    <b>To</b> {e.to.join(', ')}
                  </span>
                  {e.extracted.alertTitle ? (
                    <span>
                      <b>Alert</b> {e.extracted.alertTitle}
                    </span>
                  ) : null}
                  {e.extracted.code ? (
                    <span>
                      <b>Code</b> <code>{isAdmin ? e.extracted.code : '••••••'}</code>
                    </span>
                  ) : null}
                </div>
                {isAdmin && e.html ? (
                  <Segmented ariaLabel="Message format" value={mode} onChange={setMode} options={[{ value: 'text', label: 'Text' }, { value: 'html', label: 'Formatted' }]} />
                ) : null}
                {isAdmin && e.html && mode === 'html' ? (
                  <iframe className="email-html" title={`Email: ${e.subject}`} sandbox="allow-popups allow-popups-to-escape-sandbox" srcDoc={htmlDoc(e.html)} referrerPolicy="no-referrer" />
                ) : (
                  <pre className="email-text">{redactEmailText(e.text, e, isAdmin)}</pre>
                )}
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
